import React, { useState, useEffect } from "react";
import axios from "axios";
import ShippingSettings from "../components/ShippingSettings";
import InvoiceSettings from "../components/InvoiceSettings";
import CustomerSettings from "../components/CustomerSettings";
import DeliveryRestrictionSettings from "../components/DeliveryRestrictionSettings";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Tabs,
  Tab,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Tooltip,
  IconButton,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  Save as SaveIcon,
  Info as InfoIcon,
  Business as BusinessIcon,
  AttachMoney as CurrencyIcon,
  GetApp as AppIcon,
  Apple as AppleIcon,
  Android as AndroidIcon,
  Palette as PaletteIcon,
  Image as ImageIcon,
} from "@mui/icons-material";

// Import separate component files
import GeneralSettings from "../components/GeneralSettings";
import PaymentOptions from "../components/PaymentOptions";
import OrderSettings from "../components/OrderSettings";

const API = process.env.REACT_APP_API_URL || "https://printformee.com/api";
const BusinessSettings = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const tabsRef = React.useRef(null);

  // Handle tab change with auto-scroll
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);

    // Auto-scroll to active tab on mobile with a small delay
    if (isMobile && tabsRef.current) {
      setTimeout(() => {
        const tabsContainer = tabsRef.current;
        const tabList = tabsContainer.querySelector('[role="tablist"]');
        const activeTabElement = tabList?.querySelector(
          '[role="tab"][aria-selected="true"]',
        );

        if (activeTabElement && tabList) {
          const containerRect = tabList.getBoundingClientRect();
          const tabRect = activeTabElement.getBoundingClientRect();

          // Calculate scroll position to center the tab
          const scrollLeft =
            tabList.scrollLeft +
            (tabRect.left - containerRect.left) -
            (containerRect.width - tabRect.width) / 2;

          // Smooth scroll to center the active tab
          tabList.scrollTo({
            left: scrollLeft,
            behavior: "smooth",
          });
        }
      }, 100); // Small delay to ensure DOM is updated
    }
  };

  const [maintenanceDialogOpen, setMaintenanceDialogOpen] = useState(false);
  const [digitalProductDialogOpen, setDigitalProductDialogOpen] =
    useState(false);
  const [form, setForm] = useState({
    // System Maintenance
    maintenanceMode: false,

    // Company Information
    companyName: "PrintForMee",
    companyPhone: "+91 88799 19545",
    companyEmail: "officialprintformee@gmail.com",
    country: "India",
    timezone: "UTC",
    language: "English (En)",
    companyAddress: "Sej Plaza, Chunilal Giridharilal Marg (Marve Road) Malad West, Mumbai, Maharashtra 400064",
    latitude: "25.477972917173002",
    longitude: "77.08692478435658",

    // Business Information
    currency: "INR",
    currencyPosition: "left",
    businessModel: "single",
    pagination: 10,
    copyrightText: "Copy Right PrintForMee@2025",
    decimalPrecision: 2,
    sellDigitalProduct: false,

    // App Download Info
    showAppStore: true,
    appStoreUrl:
      "https://www.target.com/s/apple+store++now?ref=tgt_adv_XS000000&AFID=msn&fndsrc=tgtao&DFA=71700000012505188&CPNG=Electronics_Portable+Computers&adgroup=Portable+Computers&LID=700000001176246&LNM=apple+store+near+me+now&MT=b&network=s&device=c&location=12&targetid=kwd-81913773633608:loc-12&ds_rl=1246978&ds_rl=1248099&gclsrc=ds",
    showPlayStore: true,
    playStoreUrl: "https://play.google.com/store?hl=en_US&gl=US",

    // Website Colors
    primaryColor: "#1B7FED",
    secondaryColor: "#000000",

    // Logo & Branding
    headerLogo: "",
    footerLogo: "",
    favicon: "",
    loadingGif: "",
    appLogo: "",

    // Priority Setup
    brandSortingType: "default",
    brandCustomSortMethod: "latest_created",
    categorySortingType: "default",
    categoryCustomSortMethod: "latest_created",
    featuredProductsSortingType: "default",
    featuredProductsCustomSortMethod: "latest_created",
    featuredProductsStockOutHandling: "show_last",
    featuredProductsStoreStatusHandling: "show_last",
    newArrivalProductsSortingType: "default",
    newArrivalProductsCustomSortMethod: "latest_created",
    newArrivalProductsDuration: 1,
    newArrivalProductsDurationType: "days",
    newArrivalProductsStockOutHandling: "show_last",
    newArrivalProductsStoreStatusHandling: "show_last",
    topRatedProductsSortingType: "default",
    topRatedProductsCustomSortMethod: "latest_created",
    topRatedProductsRatingFilter: "4_plus",
    topRatedProductsStockOutHandling: "show_last",
    topRatedProductsStoreStatusHandling: "show_last",
    bestSellingProductsSortingType: "default",
    bestSellingProductsCustomSortMethod: "latest_created",
    bestSellingProductsStockOutHandling: "show_last",
    bestSellingProductsStoreStatusHandling: "show_last",
    productsListSearchSortingType: "default",
    productsListSearchCustomSortMethod: "latest_created",
    productsListSearchStockOutHandling: "show_last",
    productsListSearchStoreStatusHandling: "show_last",

    // Orders
    orderDeliveryVerification: true,
    minimumOrderAmount: 0,
    showBillingAddressInCheckout: true,
    freeDelivery: true,
    freeDeliveryResponsibility: "admin",
    freeDeliveryOverAmount: 500,
    refundOrderValidityDays: 1,
    guestCheckout: true,
  });

  const [paymentMethods, setPaymentMethods] = useState({
    cashOnDelivery: true,
    digitalPayment: true,
    offlinePayment: false,
  });

  const [orderSettings, setOrderSettings] = useState({
    orderDeliveryVerification: false,
    minimumOrderEnabled: true,
    minimumOrderAmount: 0,
    showBillingAddress: true,
    freeDeliveryEnabled: false,
    freeDeliveryResponsibility: "admin",
    freeDeliveryOverAmount: 0,
    refundOrderValidityDays: 7,
    guestCheckoutEnabled: false,
  });

  const fetchBusinessSettings = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/admin-settings/`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const settings = response.data;
      if (settings) {
        setForm((prev) => ({ ...prev, ...settings }));
        setPaymentMethods(
          settings.paymentMethods || {
            cashOnDelivery: true,
            digitalPayment: true,
            offlinePayment: false,
          },
        );
        setOrderSettings(
          settings.orderSettings || {
            orderDeliveryVerification: false,
            minimumOrderEnabled: true,
            minimumOrderAmount: 0,
            showBillingAddress: true,
            freeDeliveryEnabled: false,
            freeDeliveryResponsibility: "admin",
            freeDeliveryOverAmount: 0,
            refundOrderValidityDays: 7,
            guestCheckoutEnabled: false,
          },
        );
      }
      setLoading(false);
    } catch (error) {
      console.error("Error fetching business settings:", error);
      setLoading(false);
    }
  };

  useEffect(() => {
    axios
      .get(`${API}/admin-settings/`, auth())
      .then((res) => setForm((prev) => ({ ...prev, ...res.data })))
      .catch(console.error);
  }, []);

  const auth = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
  });

  const onChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    if (type === "checkbox") setForm({ ...form, [name]: checked });
    else if (type === "file") setForm({ ...form, [name]: files[0] });
    else setForm({ ...form, [name]: value });
  };

  const onSave = async () => {
    try {
      setLoading(true);
      const fd = new FormData();
      Object.keys(form).forEach(
        (k) => form[k] !== null && fd.append(k, form[k]),
      );
      await axios.post(`${API}/admin/settings`, fd, auth());
      alert("Settings saved successfully");
    } catch (e) {
      alert("Failed to save settings");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field) => (event) => {
    setForm((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handlePaymentMethodChange = (method, value) => {
    setPaymentMethods((prev) => ({
      ...prev,
      [method]: value,
    }));
  };

  const handleOrderSettingChange = (key, value) => {
    setOrderSettings((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    try {
      const token = localStorage.getItem("token");

      const updateData = {
        ...form,
        paymentMethods,
        orderSettings,
      };

      const response = await axios.put(`${API}/admin/settings`, updateData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      console.log("Business settings saved:", response.data);
      alert("Business settings saved successfully");
    } catch (error) {
      console.error("Error saving business settings:", error);
      alert("Error saving business settings");
    }
  };

  useEffect(() => {
    fetchBusinessSettings();
  }, []);

  if (loading) {
    return (
      <Box sx={{ p: 3, display: "flex", justifyContent: "center" }}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: { xs: 2, sm: 3 },
        maxWidth: "100%",
        overflow: "hidden",
      }}
    >
      <Typography
        sx={{
          mb: 3,
          fontWeight: 600,
          fontSize: { xs: "1.2rem", sm: "1.5rem", md: "1.8rem" },
          wordBreak: "break-word",
        }}
      >
        Business Settings
      </Typography>

      {/* Mobile Dropdown */}
      {isMobile && (
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Select Section</InputLabel>
          <Select
            value={activeTab}
            onChange={(e) => setActiveTab(e.target.value)}
            label="Select Section"
          >
            <MenuItem value={0}>General</MenuItem>
            <MenuItem value={1}>Payment</MenuItem>
            <MenuItem value={2}>Orders</MenuItem>
            <MenuItem value={3}>Customers</MenuItem>
            <MenuItem value={4}>Shipping</MenuItem>
            <MenuItem value={5}>Delivery</MenuItem>
            <MenuItem value={6}>Invoice</MenuItem>
          </Select>
        </FormControl>
      )}

      <Card sx={{ mb: 3, borderRadius: { xs: 1, sm: 2 } }}>
        <Tabs
          ref={tabsRef}
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          visibleScrollbar
          textColor="primary"
          indicatorColor="primary"
          sx={{
            display: { xs: "none", md: "flex" },
            borderBottom: 1,
            borderColor: "divider",
            minHeight: 48,
            "& .MuiTabs-scrollButtons": {
              color: "primary.main",
              fontSize: "1rem",
              width: 32,
              height: 32,
              borderRadius: "50%",
              backgroundColor: "rgba(25, 118, 210, 0.04)",
              "&.Mui-disabled": {
                opacity: 0.3,
                backgroundColor: "transparent",
              },
              "&:hover:not(.Mui-disabled)": {
                backgroundColor: "rgba(25, 118, 210, 0.12)",
              },
              "&:active": {
                backgroundColor: "rgba(25, 118, 210, 0.16)",
              },
            },
            "& .MuiTabs-scrollable": {
              overflowX: "auto",
              scrollbarWidth: "thin",
              "&::-webkit-scrollbar": {
                height: "4px",
              },
              "&::-webkit-scrollbar-track": {
                background: "transparent",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "rgba(0, 0, 0, 0.2)",
                borderRadius: "2px",
                "&:hover": {
                  background: "rgba(0, 0, 0, 0.3)",
                },
              },
            },
            "& .MuiTab-root": {
              minWidth: { xs: 70, sm: 100 },
              maxWidth: { xs: 110, sm: 150 },
              px: { xs: 1, sm: 1.5 },
              py: 1,
              fontSize: { xs: "0.65rem", sm: "0.75rem" },
              fontWeight: 500,
              textTransform: "none",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              minHeight: 48,
              "&:hover": {
                backgroundColor: "rgba(25, 118, 210, 0.04)",
              },
              "&.Mui-selected": {
                fontWeight: 600,
                color: "primary.main",
              },
            },
            "& .MuiTabs-indicator": {
              height: 3,
              borderRadius: "3px 3px 0 0",
            },
          }}
        >
          <Tab label="General" />
          <Tab label="Payment" />
          <Tab label="Orders" />
          <Tab label="Customers" />
          <Tab label="Shipping" />
          <Tab label="Delivery" />
          <Tab label="Invoice" />
        </Tabs>

        {/* Content Area with Safe Scroll */}
        <Box
          sx={{
            maxHeight: "calc(100vh - 200px)",
            overflowY: "auto",
            pr: 1,
          }}
        >
          <Box
            sx={{
              p: { xs: 1, sm: 2 },
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            {activeTab === 0 && <GeneralSettings />}
            {activeTab === 1 && <PaymentOptions />}
            {activeTab === 2 && <OrderSettings />}
            {activeTab === 3 && <CustomerSettings />}
            {activeTab === 4 && <ShippingSettings />}
            {activeTab === 5 && <DeliveryRestrictionSettings />}
            {activeTab === 6 && <InvoiceSettings />}
          </Box>
        </Box>

        {/* Enhanced Save Button */}
        <Box
          sx={{
            px: { xs: 2, sm: 3 },
            py: 2,
            borderTop: 1,
            borderColor: "divider",
            backgroundColor: "#fff",
            position: { xs: "sticky", sm: "relative" },
            bottom: 0,
            zIndex: 10,
            pt: 2,
            mt: 2,
          }}
        >
          <Button
            variant="contained"
            color="primary"
            size={isMobile ? "medium" : "large"}
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={loading}
            fullWidth={isMobile}
            sx={{
              px: { xs: 2, sm: 3 },
              py: { xs: 1, sm: 1.5 },
              fontSize: { xs: "0.875rem", sm: "1rem" },
              fontWeight: 600,
              textTransform: "none",
              borderRadius: { xs: 1, sm: 2 },
              boxShadow: { xs: 2, sm: 1 },
              "&:hover": {
                boxShadow: { xs: 4, sm: 2 },
              },
              "&:disabled": {
                backgroundColor: "action.disabled",
                color: "action.disabled",
              },
            }}
          >
            {loading ? (
              <>
                <CircularProgress size={16} sx={{ mr: 1 }} />
                Saving...
              </>
            ) : (
              "Save Settings"
            )}
          </Button>
        </Box>
      </Card>
    </Box>
  );
};

export default BusinessSettings;
